import { footerLine, footerParts, plain } from './helpers'

/**
 * The shul's own letterhead and foot — the two halves of one stationery.
 *
 * Every sheet this platform prints on a congregation's paper opens and closes
 * with these: the wall luach, the year table, a notice on the door. They are
 * one component rather than two because a preset styles them as one figure —
 * `split` runs a rule through the name AND through the address, and a head that
 * moved without its foot would leave the sheet opening on one mark and closing
 * on another.
 *
 * The classes a preset puts on the artifact's ROOT (`letterheadClasses`) decide
 * how these draw, so a caller renders `<Stationery>` and states nothing about
 * how it looks.
 */

/** Everything the stationery states about the congregation. */
export type StationeryShul = Pick<
  Parameters<typeof footerParts>[0],
  'name' | 'addressLine' | 'city' | 'region' | 'postalCode' | 'phone' | 'email'
> & {
  nameHe?: string | null
  rabbiLineHe?: string | null
  rabbiLineHeResolved?: string | null
  letterhead?: string | null
}

/**
 * The head: the congregation's name over the Rav's line.
 *
 * Renders nothing at all where the shul has neither. A rule under an empty
 * block is a mark the reader has to account for and cannot.
 */
export function StationeryHead({ shul }: { shul: StationeryShul }) {
  // The rov-role-derived line wins over the manual override.
  const rav = shul.rabbiLineHeResolved || shul.rabbiLineHe
  if (!shul.nameHe && !rav) return null
  return (
    <div className="shul-letterhead">
      {shul.nameHe ? (
        <div className="shul-name">{plain(shul.nameHe)}</div>
      ) : null}
      {rav ? <div className="shul-rav">{plain(rav)}</div> : null}
    </div>
  )
}

/**
 * The foot: where the shul is and how to reach it.
 *
 * The address sits in its own element so a form that runs its rule THROUGH the
 * line — split, inline, measured — has something to run either side of. The
 * contact half is a sibling rather than a child, so those forms rule one line
 * and not both.
 *
 * `twoLine` is the artifact's call, not the stationery's: a sheet with room
 * gives the address and the contact a line each, and one short of room takes
 * them joined.
 */
export function StationeryFoot({
  shul,
  twoLine = false,
}: {
  shul: StationeryShul
  twoLine?: boolean
}) {
  const foot = footerParts(shul)
  return (
    <footer className="shul-foot">
      <span className="shul-footline">
        {twoLine ? foot.place : footerLine(shul)}
      </span>
      {twoLine ? (
        <span className="shul-footcontact">{foot.contact}</span>
      ) : null}
    </footer>
  )
}
